import React, { MutableRefObject, useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";

import { faExclamationCircle } from "@fortawesome/pro-solid-svg-icons/faExclamationCircle";
import { faFunnelDollar } from "@fortawesome/pro-solid-svg-icons/faFunnelDollar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Card from "antd/es/card";
import Col from "antd/es/col";
import { FormInstance } from "antd/es/form";
import notification from "antd/es/notification";
import Row from "antd/es/row";
import cn from "classnames";
import { useIntl } from "react-intl";
import { useHistory } from "react-router-dom";

import DealStageType from "@mapmycustomers/shared/enum/DealStageType";
import { Deal } from "@mapmycustomers/shared/types/entity";
import Stage from "@mapmycustomers/shared/types/entity/deals/Stage";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { Alert, Button } from "@mapmycustomers/ui";

import StagesCarousel from "@app/component/StagesCarousel";
import LongContentParagraph from "@app/component/typography/LongContentParagraph";
import DealClosedDateSettingValue from "@app/enum/DealClosedDateSettingValue";
import OrganizationSetting from "@app/enum/OrganizationSetting";
import RecordPaneTab from "@app/enum/preview/RecordPaneTab";
import { getOrganizationSettingValue, isCurrentUserReadOnly } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import layout from "@app/styles/layout";
import showClosingDateAlert from "@app/util/deal/showClosingDateAlert";
import dealFieldModel, { DealFieldName } from "@app/util/fieldModel/DealFieldModel";
import EditDealFormValues from "@app/util/form/EditDealFormValues";
import { formatDate } from "@app/util/formatters";
import isClosedStage from "@app/util/isClosedStage";
import { dealLayoutModel } from "@app/util/layout/impl";

import { dealAnalytics } from "../../analytics";
import { updateDeal } from "../store/actions";
import { isDealUpdateLoading } from "../store/selectors";

import AmountField from "./components/AmountField";
import ClosingDateField from "./components/ClosingDateField";
import useActions from "./components/useActions";
import WonOverlay from "./components/WonOverlay";
import messages from "./messages";
import styles from "./QuickDealEdit.module.scss";

interface Props {
  activeTab: RecordPaneTab | undefined;
  closedDateUpdateSetting: DealClosedDateSettingValue;
  deal: Deal;
  detailsForm: FormInstance<EditDealFormValues>;
  formFieldsRef: MutableRefObject<any>;
  handleFinishFail: () => void;
  isReadOnlyMember: boolean;
  loading: boolean;
  onFormChange: (isChanged: boolean) => void;
  onUpdateDeal: typeof updateDeal.request;
  stages: Stage[];
}

const QuickDealEdit: React.FC<Props> = ({
  activeTab,
  closedDateUpdateSetting,
  deal,
  detailsForm,
  formFieldsRef,
  handleFinishFail,
  isReadOnlyMember,
  loading,
  onFormChange,
  onUpdateDeal,
  stages,
}) => {
  const intl = useIntl();
  const history = useHistory();

  const showDetailsTab = useCallback(() => {
    history.push(RecordPaneTab.DETAILS);
  }, [history]);

  const doesDealHaveMissingFields = useCallback(
    (stage: Stage) => dealLayoutModel.hasMissingRequiredFieldsFor({ ...deal, stage }),
    [deal]
  );

  const handleUpdateForm = useCallback(
    (stage: Stage) => {
      detailsForm.setFieldValue("stageId", stage.id);
      onFormChange(true);
      detailsForm.validateFields().catch(() => {
        handleFinishFail();
      });
      setNewSelectedStage(undefined);
    },
    [detailsForm, handleFinishFail, onFormChange]
  );

  const [wonOverlayVisible, showWonOverlay, hideWonOverlay] = useBoolean();

  const updateClosedDeal = useCallback(
    (closingStage: Stage, closingDate: null | string) =>
      onUpdateDeal({
        callback: (updatedDeal: Deal) => {
          if (updatedDeal.stage.type === DealStageType.WON) {
            showWonOverlay();
          }
        },
        deal: { ...deal, closingDate, stage: closingStage },
        layoutId: dealLayoutModel.getLayoutFor(deal).id,
      }),
    [deal, onUpdateDeal, showWonOverlay]
  );

  const onCloseDeal = useCallback(
    (stageType: DealStageType.LOST | DealStageType.WON) => {
      const closingStage = stages.find(({ type }) => type === stageType);
      if (!closingStage) {
        return; // must not happen
      }
      const today = new Date();

      if (!doesDealHaveMissingFields(closingStage)) {
        if (
          closedDateUpdateSetting === DealClosedDateSettingValue.ASK_EVERYTIME &&
          deal.closingDate
        ) {
          showClosingDateAlert(
            () => {
              updateClosedDeal(closingStage, formatDate(today, "yyyy-MM-dd"));
            },
            () => {
              updateClosedDeal(closingStage, deal.closingDate);
            },
            intl
          );
        } else {
          updateClosedDeal(
            closingStage,
            closedDateUpdateSetting === DealClosedDateSettingValue.CHANGE_TO_TODAY
              ? formatDate(today, "yyyy-MM-dd")
              : deal.closingDate
          );
        }
      } else {
        if (activeTab === RecordPaneTab.DETAILS) {
          handleUpdateForm(closingStage);
        } else {
          showDetailsTab();
        }
      }
    },
    [
      activeTab,
      closedDateUpdateSetting,
      deal.closingDate,
      doesDealHaveMissingFields,
      handleUpdateForm,
      intl,
      showDetailsTab,
      stages,
      updateClosedDeal,
    ]
  );

  const handleStageWon = useCallback(() => {
    dealAnalytics.clicked(["Marked Won"]);
    onCloseDeal(DealStageType.WON);
  }, [onCloseDeal]);

  const handleStageLost = useCallback(() => {
    dealAnalytics.clicked(["Marked Lost"]);
    onCloseDeal(DealStageType.LOST);
  }, [onCloseDeal]);

  const handleStageReOpen = useCallback(() => {
    const reOpenStage = stages
      .filter((stage) => !isClosedStage(stage))
      .reduce((result, item) => (item.displayOrder > result.displayOrder ? item : result));

    if (!doesDealHaveMissingFields(reOpenStage)) {
      onUpdateDeal({
        deal: { ...deal, stage: reOpenStage },
        layoutId: dealLayoutModel.getLayoutFor(deal).id,
      });
    } else if (activeTab === RecordPaneTab.DETAILS) {
      handleUpdateForm(reOpenStage);
    } else {
      showDetailsTab();
    }
  }, [
    activeTab,
    deal,
    doesDealHaveMissingFields,
    handleUpdateForm,
    onUpdateDeal,
    showDetailsTab,
    stages,
  ]);

  const handleAmountChange = useCallback(
    (amount: Deal["amount"]) => {
      const schema = dealLayoutModel.getSchemaFor(deal);
      const amountSchemaField = schema.find(({ field }) => field === DealFieldName.AMOUNT);
      if (amountSchemaField?.required && amount == null) {
        notification.error({ message: intl.formatMessage(messages.amountRequired) });
        return;
      }
      // not using layoutId in this request to allow partial put
      onUpdateDeal({ deal: { id: deal.id, amount } });
    },
    [deal, intl, onUpdateDeal]
  );

  const handleClosingDateChange = useCallback(
    (closingDate: Deal["closingDate"]) => {
      const schema = dealLayoutModel.getSchemaFor(deal);
      const closingDateSchemaField = schema.find(
        ({ field }) => field === DealFieldName.CLOSING_DATE
      );
      if (closingDateSchemaField?.required && closingDate == null) {
        notification.error({ message: intl.formatMessage(messages.closingDateRequired) });
        return;
      }
      // not using layoutId in this request to allow partial put
      onUpdateDeal({ deal: { id: deal.id, closingDate } });
    },
    [deal, intl, onUpdateDeal]
  );

  const [newSelectedStage, setNewSelectedStage] = useState<Stage | undefined>(deal.stage);

  // reset when deal changes
  useEffect(() => setNewSelectedStage(undefined), [deal]);

  const handleStageChange = useCallback(
    (stage: Stage) => {
      dealAnalytics.clicked(["Moved Stage Carousel"]);
      if (doesDealHaveMissingFields(stage)) {
        setNewSelectedStage(stage);
      } else {
        setNewSelectedStage(undefined);
        onUpdateDeal({
          deal: { ...deal, stage },
          layoutId: dealLayoutModel.getLayoutFor(deal).id,
        });
      }
    },
    [deal, doesDealHaveMissingFields, onUpdateDeal]
  );

  const handleFillOutClick = useCallback(() => {
    if (newSelectedStage) {
      if (activeTab === RecordPaneTab.DETAILS) {
        handleUpdateForm(newSelectedStage);
      } else {
        showDetailsTab();
      }
    }
  }, [activeTab, handleUpdateForm, newSelectedStage, showDetailsTab]);

  useEffect(() => {
    if (!formFieldsRef.current) {
      return;
    }
    const config = { attributes: true, childList: true, subtree: true };
    const mo = new MutationObserver(() => {
      const el = document.querySelectorAll("[data-id='js-funnel-staging-field']");
      if (activeTab === RecordPaneTab.DETAILS && newSelectedStage && el.length > 0) {
        handleUpdateForm(newSelectedStage);
      }
    });
    mo.observe(formFieldsRef.current, config);

    return () => {
      mo.disconnect();
    };
  }, [activeTab, detailsForm, newSelectedStage, onFormChange, formFieldsRef, handleUpdateForm]);

  const actions = useActions(deal, handleStageLost, handleStageWon, handleStageReOpen);

  return (
    <>
      <WonOverlay onHide={hideWonOverlay} visible={wonOverlayVisible} />
      <Card
        actions={isReadOnlyMember ? undefined : actions}
        className={cn(styles.container, {
          [styles.closedDeal]: isClosedStage(deal.stage),
          [styles.missingFields]: !!newSelectedStage,
        })}
        title={
          <div className={styles.funnel}>
            <FontAwesomeIcon className={styles.funnelIcon} icon={faFunnelDollar} />
            {deal.funnel.name}
          </div>
        }
      >
        <StagesCarousel
          className={styles.stages}
          disabled={isReadOnlyMember}
          loading={loading}
          onChange={handleStageChange}
          stages={stages}
          value={deal.stage}
        />

        {newSelectedStage && (
          <div className={styles.alert}>
            <Alert
              action={
                <Button onClick={handleFillOutClick} size="small" type="primary">
                  {intl.formatMessage(messages.missingFieldsButton)}
                </Button>
              }
              icon={<FontAwesomeIcon icon={faExclamationCircle} />}
              message={intl.formatMessage(messages.missingFieldsText)}
              showIcon
              type="error"
            />
          </div>
        )}

        <Row className={styles.content} gutter={layout.spacerS}>
          <Col span={12}>
            {dealFieldModel.getByName(DealFieldName.AMOUNT)?.isReadable && (
              <AmountField
                disabled={
                  !dealFieldModel.getByName(DealFieldName.AMOUNT)?.isEditable || isReadOnlyMember
                }
                onChange={handleAmountChange}
                value={deal.amount}
              />
            )}
          </Col>
          <Col span={12}>
            {dealFieldModel.getByName(DealFieldName.CLOSING_DATE)?.isReadable && (
              <ClosingDateField
                deal={deal}
                disabled={
                  !dealFieldModel.getByName(DealFieldName.CLOSING_DATE)?.isEditable ||
                  isReadOnlyMember
                }
                onChange={handleClosingDateChange}
              />
            )}
          </Col>
        </Row>
        {deal.stage.type === DealStageType.LOST && (
          <>
            <Row className={styles.content} gutter={layout.spacerS}>
              <Col span={12}>
                <div className={styles.lossDeal}>{intl.formatMessage(messages.lossReason)}</div>
              </Col>
              <Col span={12}>
                <div className={styles.dealLossReason}>{deal.dealLossReason?.name}</div>
              </Col>
            </Row>
            <Row className={cn(styles.dealLossComment, styles.content)} gutter={layout.spacerS}>
              <Col span={12}>
                <div className={styles.lossDeal}>{intl.formatMessage(messages.lossComment)}</div>
              </Col>
              <Col span={12}>
                <LongContentParagraph noMargin rows={2}>
                  <span
                    className={deal.dealLossComment ? styles.lossComment : styles.disabledNoComment}
                  >
                    {deal.dealLossComment ?? intl.formatMessage(messages.noComment)}
                  </span>
                </LongContentParagraph>
              </Col>
            </Row>
          </>
        )}
      </Card>
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  closedDateUpdateSetting: getOrganizationSettingValue(state)(
    OrganizationSetting.CLOSED_DATE_UPDATE_PREFERENCE
  ),
  isReadOnlyMember: isCurrentUserReadOnly(state),
  loading: isDealUpdateLoading(state),
});

const mapDispatchToProps = {
  onUpdateDeal: updateDeal.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(QuickDealEdit);
